// Factory for the per-resource list query hook (useContacts / useOrganisations).
// Preserves the shipped behaviour: previous data is held across a key change so
// pagination / sort changes don't flash an empty table, a 30s staleTime, and an
// optional SSR initialData seed for a fast first paint.

import { useQuery } from '@tanstack/react-query';

const STALE_TIME = 30_000;

export interface ResourceListQueryConfig<TResponse, TParams> {
  // Cache key for a given param set (the resource's queryKeys.list).
  queryKey: (params: TParams) => readonly unknown[];
  // Fetches a page of the list from the API.
  fetcher: (params: TParams) => Promise<TResponse>;
  // The parent a list hangs off, for params that carry one. Previous data is
  // then held only while this value holds: paging one parent keeps the rows on
  // screen, switching parents does not render one parent's rows under another.
  // Omit for a list whose params are filters alone.
  identity?: (params: TParams) => unknown;
}

export interface UseResourceListQueryOptions<TResponse, TParams> {
  params?: TParams;
  initialData?: TResponse;
}

export function createResourceListQuery<TResponse, TParams extends object>(
  config: ResourceListQueryConfig<TResponse, TParams>,
) {
  return function useResourceListQuery({
    params,
    initialData,
  }: UseResourceListQueryOptions<TResponse, TParams> = {}) {
    const resolvedParams = (params ?? {}) as TParams;
    const identity = config.identity?.(resolvedParams);
    return useQuery({
      queryKey: config.queryKey(resolvedParams),
      queryFn: () => config.fetcher(resolvedParams),
      staleTime: STALE_TIME,
      // Read back off the outgoing query to tell which parent its data came from.
      meta: { listIdentity: identity },
      placeholderData: (previousData, previousQuery) =>
        !config.identity || previousQuery?.meta?.listIdentity === identity
          ? previousData
          : undefined,
      ...(initialData ? { initialData, initialDataUpdatedAt: Date.now() } : {}),
    });
  };
}
